import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { flows, responses, courses } from '../services/api';
import QuestionCard from './QuestionCard';
import PDFQuestion from './questions/PDFQuestion';
import VideoPlayerQuestion from './questions/VideoPlayerQuestion';
import { FlowProgress, Question, ResponsePayload } from '../types/index';
import { useAuth } from '../contexts/AuthContext';
import { QuestionLayout } from './layout/QuestionLayout';
import { Box, LinearProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getTestId } from '../utils/testUtils';
import ImagePreloader from './ImagePreloader';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { ConfettiEffect } from './ConfettiEffect';

interface ProgressIndicatorProps {
  isMobile?: boolean;
}

const ProgressIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<ProgressIndicatorProps>(({ theme, isMobile }) => ({
  position: 'fixed',
  top: theme.spacing(isMobile ? 2 : 4),
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: 'rgba(38, 38, 38, 0.8)',
  padding: theme.spacing(isMobile ? 1.5 : 2, isMobile ? 2 : 4),
  borderRadius: '30px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  zIndex: 1000,
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  minWidth: isMobile ? '90%' : '450px',
  maxWidth: '90%',
}));

interface ProgressContainerProps {
  isMobile?: boolean;
}

const ProgressContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<ProgressContainerProps>(({ isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '8px' : '16px',
  width: '100%'
}));

interface StyledLinearProgressProps {
  isMobile?: boolean;
}

const StyledLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<StyledLinearProgressProps>(({ theme, isMobile }) => ({
  height: isMobile ? 6 : 8,
  borderRadius: 4,
  flexGrow: 1,
  minWidth: isMobile ? '150px' : '300px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    background: 'linear-gradient(90deg, #e66ca8 0%, #374fff 50%, #62c4ba 100%)',
    backgroundSize: '200% 100%',
    animation: 'gradient-shift 3s ease infinite',
  },
  '@keyframes gradient-shift': {
    '0%': {
      backgroundPosition: '0% 50%'
    },
    '50%': {
      backgroundPosition: '100% 50%'
    },
    '100%': {
      backgroundPosition: '0% 50%'
    }
  }
}));

interface ProgressTextProps {
  isMobile?: boolean;
}

const ProgressText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<ProgressTextProps>(({ theme, isMobile }) => ({
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '8px' : '12px',
  whiteSpace: 'nowrap',
  fontSize: isMobile ? '0.85rem' : '1rem',
  '& .percentage': {
    color: theme.palette.text.primary,
    fontSize: isMobile ? '0.9rem' : '1rem',
  },
  '& .flow-name': {
    color: theme.palette.text.primary,
    fontSize: isMobile ? '0.9rem' : '1rem',
    display: isMobile ? 'none' : 'inline',
  }
}));

interface QuestionContainerProps {
  isMobile?: boolean;
}

const QuestionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<QuestionContainerProps>(({ theme, isMobile }) => ({
  marginBottom: theme.spacing(isMobile ? 3 : 6),
  scrollMarginTop: theme.spacing(2),
  width: '100%',
}));

interface QuestionFlowProps {
  flowName: string;
}

interface AnsweredQuestion {
  question: Question;
  response?: ResponsePayload;
}

interface RemainingQuestionsData {
  status: string;
  current_index: number;
  total_questions: number;
  remaining_questions: Question[];
  flow: {
    id: number;
    name: string;
  };
}

const QuestionFlow: React.FC<QuestionFlowProps> = ({ flowName }) => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { isMobile } = useResponsiveLayout();
  const [flowProgress, setFlowProgress] = useState<FlowProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questions, setQuestions] = useState<AnsweredQuestion[]>([]);
  const [remainingQuestions, setRemainingQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const latestQuestionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initializeFlow = async () => {
      try {
        setLoading(true);
        
        if (flowName === 'intake_assessment_v0' && user?.is_staff && user?.has_completed_intake) {
          await flows.resetFlow(flowName);
        }
        
        if (flowName === 'daily_checkin_v0') {
          await flows.restartFlow(flowName);
          const progress = await flows.getProgress(flowName);
          setFlowProgress(progress.data);
          
          if (progress.data?.question) {
            setQuestions([{
              question: progress.data.question as Question,
            }]);
          }
          
          // Fetch all remaining questions for this flow
          const remainingQuestionsResponse = await flows.getRemainingQuestionDetails(flowName);
          const remainingData = remainingQuestionsResponse.data as RemainingQuestionsData;
          
          if (remainingData && remainingData.remaining_questions) {
            // Store all remaining questions except the first one (which we'll show separately)
            // This prevents duplication of the first question
            const firstQuestion = remainingData.remaining_questions[0];
            const restOfQuestions = remainingData.remaining_questions.slice(1);
            
            setRemainingQuestions(restOfQuestions);
            setCurrentQuestionIndex(remainingData.current_index);
            setTotalQuestions(remainingData.total_questions);
          }
          
          setLoading(false);
          return;
        }
        
        const currentProgress = await flows.getProgress(flowName);
        
        if (currentProgress.data?.status === 'completed' && 
            !(flowName === 'intake_assessment_v0' && user?.is_staff)) {
          if (flowName === 'intake_assessment_v0') {
            updateUser({ has_completed_intake: true });
          }
          navigate('/home');
          return;
        }
        
        // Fetch all remaining questions for this flow
        const remainingQuestionsResponse = await flows.getRemainingQuestionDetails(flowName);
        const remainingData = remainingQuestionsResponse.data as RemainingQuestionsData;
        
        if (remainingData && remainingData.remaining_questions) {
          // Store all remaining questions except the first one (which we'll show separately)
          // This prevents duplication of the first question
          const firstQuestion = remainingData.remaining_questions[0];
          const restOfQuestions = remainingData.remaining_questions.slice(1);
          
          setRemainingQuestions(restOfQuestions);
          setCurrentQuestionIndex(remainingData.current_index);
          setTotalQuestions(remainingData.total_questions);
          
          // Initialize with just the first question
          if (firstQuestion) {
            if (currentProgress.data) {
              setFlowProgress(currentProgress.data);
              
              setQuestions([{
                question: firstQuestion,
              }]);
            } else {
              const progress = await flows.startFlow(flowName);
              setFlowProgress(progress);
              
              setQuestions([{
                question: firstQuestion,
              }]);
            }
          }
        } else if (currentProgress.data) {
          setFlowProgress(currentProgress.data);
          
          if (currentProgress.data?.question) {
            setQuestions([{
              question: currentProgress.data.question as Question,
            }]);
          }
        } else {
          const progress = await flows.startFlow(flowName);
          setFlowProgress(progress);
          
          if (progress?.question) {
            setQuestions([{
              question: progress.question as Question,
            }]);
          }
        }
      } catch (err) {
        console.error('Failed to initialize flow:', err);
        setError('Failed to initialize flow');
      } finally {
        setLoading(false);
      }
    };

    initializeFlow();
  }, [flowName, navigate, updateUser, user]);

  useEffect(() => {
    // Add a 2 second delay before scrolling to ensure the DOM has updated
    if (latestQuestionRef.current && questions.length > 0) {
      // Use a timeout to allow the browser to render everything first
      const timeoutId = setTimeout(() => {
        latestQuestionRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 1500); 
      
      return () => clearTimeout(timeoutId);
    }
  }, [questions.length]);

  const handleSubmit = async (response: any) => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Create the response in the backend
      await responses.create({
        question: flowProgress?.question?.id,
        ...response
      });

      // Mark the current question as answered in our local state
      setQuestions(prevQuestions => 
        prevQuestions.map((q, index) => 
          index === prevQuestions.length - 1 
            ? { ...q, response } 
            : q
        )
      );

      // Update the progress in the backend (async)
      const progressUpdatePromise = flows.updateProgress(flowName, {
        question_id: flowProgress?.question?.id || 0
      });

      // Increment our local current question index
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      
      // Log the current state before updating
      console.log('[QuestionFlow] Before updating remainingQuestions:', {
        remainingQuestionsLength: remainingQuestions.length,
        currentIndex: currentQuestionIndex,
        nextIndex
      });

      // Check if we've reached the end of the flow
      if (nextIndex >= totalQuestions) {
        // Wait for the progress update to complete
        const progressResponse = await progressUpdatePromise;
        
        // Check if this is the intake assessment
        if (flowName === 'intake_assessment_v0') {
          updateUser({ has_completed_intake: true });
          navigate('/reports');
          return;
        }
        
        // Handle flow completion
        const handleFlowCompletion = async () => {
          try {
            // Show confetti effect before navigating
            setShowConfetti(true);
            
            // First check if it's daily_checkin_v0
            if (flowName === 'daily_checkin_v0') {
              // Add a small delay to let users see the confetti
              setTimeout(() => {
                navigate('/daily-checkin-complete');
              }, 1500);
              return;
            }

            // Then check if it's part of a course
            const courseResponse = await courses.getFlowByName(flowName);
            const courseData = courseResponse.data;
            
            if (courseData.course) {
              // Add a small delay to let users see the confetti
              setTimeout(() => {
                // Navigate to course continue page with state
                navigate('/course-continue', {
                  state: {
                    completedFlowName: flowName,
                    completedFlowId: courseData.flow?.id,
                    courseId: courseData.course.id
                  },
                  replace: true  // Use replace to prevent back button from returning to the flow
                });
              }, 1500);
              return;
            }
            
            // Not part of a course - handle normal flow completion
            if (flowName === 'intake_assessment_v0') {
              await updateUser({ has_completed_intake: true });
            }
            // Add a small delay to let users see the confetti
            setTimeout(() => {
              navigate('/home');
            }, 1500);
          } catch (error) {
            console.error('Error handling flow completion:', error);
            // If there's an error checking course status, default to home
            setTimeout(() => {
              navigate('/home');
            }, 1500);
          }
        };
        
        // Execute flow completion handling
        handleFlowCompletion();
        return;
      }
      
      // If we have remaining questions, show the next one immediately
      // Log the current state to help with debugging
      console.log('[QuestionFlow] Showing next question:', {
        currentQuestionIndex,
        nextIndex,
        remainingQuestionsLength: remainingQuestions.length
      });
      
      // With our new structure, remainingQuestions only contains future questions
      // So we can directly use the first question from the array
      if (remainingQuestions.length > 0) {
        // Get the first question from the remaining questions array
        const nextQuestion = remainingQuestions[0];
        
        // Remove this question from the remaining questions array
        setRemainingQuestions(prevQuestions => prevQuestions.slice(1));
        
        // Add the next question to our questions array
        setQuestions(prevQuestions => [
          ...prevQuestions,
          {
            question: nextQuestion,
          }
        ]);
        
        // Update the flow progress in the background
        progressUpdatePromise.then(progressResponse => {
          setFlowProgress(progressResponse.data);
        }).catch(error => {
          console.error('Error updating flow progress:', error);
        });
      } else {
        // If we don't have the next question cached, wait for the progress update
        const progressResponse = await progressUpdatePromise;
        setFlowProgress(progressResponse.data);
        
        // Fetch the next question from the backend
        const nextProgress = await flows.getProgress(flowName);
        setFlowProgress(nextProgress.data);
        
        if (nextProgress.data?.question) {
          setQuestions(prevQuestions => [
            ...prevQuestions,
            {
              question: nextProgress.data.question as Question,
            }
          ]);
        }
      }

    } catch (error) {
      console.error('Error submitting response:', error);
      setError('Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuestionComplete = () => {
    handleSubmit({} as ResponsePayload);
  };

  const handleValidationChange = () => {};
  
  // Function to progress to the next question without submitting a response
  const progressWithoutSubmitting = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Skip creating a response in the backend
      
      // Update the progress in the backend (async)
      const progressUpdatePromise = flows.updateProgress(flowName, {
        question_id: flowProgress?.question?.id || 0
      });

      // Increment our local current question index
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      
      // Log the current state before updating
      console.log('[QuestionFlow] Progressing without submitting, showing next question:', {
        remainingQuestionsLength: remainingQuestions.length,
        currentIndex: currentQuestionIndex,
        nextIndex
      });

      // Check if we've reached the end of the flow
      if (nextIndex >= totalQuestions) {
        // Handle flow completion similar to handleSubmit
        const progressResponse = await progressUpdatePromise;
        
        if (flowName === 'intake_assessment_v0') {
          updateUser({ has_completed_intake: true });
          navigate('/reports');
          return;
        }
        
        // Show confetti effect before navigating
        setShowConfetti(true);
        
        // Handle different flow types
        if (flowName === 'daily_checkin_v0') {
          setTimeout(() => {
            navigate('/daily-checkin-complete');
          }, 1500);
          return;
        }

        // Check if it's part of a course
        const courseResponse = await courses.getFlowByName(flowName);
        const courseData = courseResponse.data;
        
        if (courseData.course) {
          setTimeout(() => {
            navigate('/course-continue', {
              state: {
                completedFlowName: flowName,
                completedFlowId: courseData.flow?.id,
                courseId: courseData.course.id
              },
              replace: true
            });
          }, 1500);
          return;
        }
        
        // Default navigation
        setTimeout(() => {
          navigate('/home');
        }, 1500);
        return;
      }
      
      // If we have remaining questions, show the next one immediately
      if (remainingQuestions.length > 0) {
        // Get the first question from the remaining questions array
        const nextQuestion = remainingQuestions[0];
        
        // Remove this question from the remaining questions array
        setRemainingQuestions(prevQuestions => prevQuestions.slice(1));
        
        // Add the next question to our questions array
        setQuestions(prevQuestions => [
          ...prevQuestions,
          {
            question: nextQuestion,
          }
        ]);
        
        // Update the flow progress in the background
        progressUpdatePromise.then(progressResponse => {
          setFlowProgress(progressResponse.data);
        }).catch(error => {
          console.error('Error updating flow progress:', error);
        });
      } else {
        // If we don't have the next question cached, wait for the progress update
        const progressResponse = await progressUpdatePromise;
        setFlowProgress(progressResponse.data);
        
        // Fetch the next question from the backend
        const nextProgress = await flows.getProgress(flowName);
        setFlowProgress(nextProgress.data);
        
        if (nextProgress.data?.question) {
          setQuestions(prevQuestions => [
            ...prevQuestions,
            {
              question: nextProgress.data.question as Question,
            }
          ]);
        }
      }
    } catch (error) {
      console.error('Error progressing to next question:', error);
      setError('Failed to progress to the next question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pre-render the next question if available
  const getNextQuestionToPrerender = () => {
    // After the initialization change, remainingQuestions only contains future questions
    // (not including the current one), so the next question is at index 0
    console.log('[QuestionFlow] Pre-rendering check:', {
      remainingQuestionsLength: remainingQuestions.length,
      currentQuestionIndex,
      totalQuestions
    });
    
    if (remainingQuestions.length > 0) {
      return remainingQuestions[0];
    }
    return null;
  };
  
  const renderQuestion = (questionItem: AnsweredQuestion, index: number) => {
    const isLatest = index === questions.length - 1;
    const { question, response } = questionItem;
    
    // Determine if this question has already been submitted
    // A question is considered submitted if it has a response or if it's not the latest question
    const isSubmitted = !!response || !isLatest;
    
    return (
      <QuestionContainer 
        key={`${question.id}-${index}`}
        ref={isLatest ? latestQuestionRef : null}
        isMobile={isMobile}
        {...getTestId(`question-container-${question.id}`)}
      >
        <QuestionCard 
          question={question} 
          onSubmit={isSubmitted ? async () => {} : handleSubmit} // Empty function for submitted questions
          flowName={flowName}
          isSubmitting={isSubmitting && isLatest}
          onProgressWithoutSubmitting={isLatest ? progressWithoutSubmitting : undefined} // Pass the function only to the latest question
        />
      </QuestionContainer>
    );
  };
  
  // Render the next question in a hidden container for preloading
  const renderNextQuestion = () => {
    const nextQuestion = getNextQuestionToPrerender();
    if (!nextQuestion) return null;
    
    // Create a dummy submit function that returns a Promise to match the expected type
    const dummySubmit = async () => { return Promise.resolve(); };
    
    return (
      <div style={{ display: 'none', visibility: 'hidden' }}>
        <QuestionCard 
          question={nextQuestion} 
          onSubmit={dummySubmit} // Dummy function that returns a Promise
          flowName={flowName}
          isSubmitting={false}
        />
      </div>
    );
  };

  const calculateProgress = () => {
    // Use our local state variables if available, otherwise fall back to flowProgress
    if (totalQuestions > 0 && typeof currentQuestionIndex === 'number') {
      return Math.round((currentQuestionIndex / totalQuestions) * 100);
    } else if (flowProgress?.total_questions && typeof flowProgress?.current_index === 'number') {
      return Math.round((flowProgress.current_index / flowProgress.total_questions) * 100);
    }
    return 0;
  };

  if (loading) {
    return (
      <QuestionLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <LinearProgress />
        </Box>
      </QuestionLayout>
    );
  }

  if (error) {
    return (
      <QuestionLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography color="error">Error: {error}</Typography>
        </Box>
      </QuestionLayout>
    );
  }

  if (!flowProgress?.question && questions.length === 0) {
    return (
      <QuestionLayout {...getTestId(`flow-${flowName}`)}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography {...getTestId('flow-no-questions-message')}>No questions available</Typography>
        </Box>
      </QuestionLayout>
    );
  }

  return (
    <QuestionLayout {...getTestId(`flow-${flowName}`)}>
      {/* Preload images for upcoming questions */}
      <ImagePreloader questions={remainingQuestions} />
      
      {/* Show confetti when the flow is completed */}
      {showConfetti && <ConfettiEffect />}
      
      <ProgressIndicator isMobile={isMobile} {...getTestId('flow-progress-indicator')}>
        <ProgressContainer isMobile={isMobile}>
          <StyledLinearProgress
            isMobile={isMobile}
            variant="determinate"
            value={calculateProgress()}
            {...getTestId('flow-progress-bar')}
          />
          <ProgressText isMobile={isMobile}>
            <span className="percentage" {...getTestId('flow-progress-percentage')}>{calculateProgress()}%</span>
            <span className="flow-name" {...getTestId('flow-name')}>{flowName}</span>
          </ProgressText>
        </ProgressContainer>
      </ProgressIndicator>
      
      <Box 
        sx={{
          width: '100%',
          maxWidth: questions.some(q => q.question.question_type === 'pdf') ? 
            (isMobile ? '95vw' : '80vw') : 
            (isMobile ? '100%' : '800px'), 
          margin: '0 auto', 
          pt: isMobile ? 10 : 12,  // Adjusted top padding for mobile
          pb: isMobile ? 6 : 8,
          px: isMobile ? 1 : 3,   // Reduced horizontal padding for mobile
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 120px)' // Account for padding
        }}
        {...getTestId('flow-questions-container')}
      >
        {questions.map((questionItem, index) => renderQuestion(questionItem, index))}
        
        {/* Pre-render the next question for faster loading */}
        {renderNextQuestion()}
      </Box>
    </QuestionLayout>
  );
};

export default QuestionFlow;